import type { CachedCaster } from './types';
import { abilityEntriesOfType, abilityEntryId, abilityEntryCharges, abilityEntryMaxCharges, passiveActive } from './tabs/ability-row';
import { sheetDefinition } from './k5-runtime';
import { chargeTicksFor, heartbeatEncounterReasons } from '../janus-engine/turns/time-push';
import { statusRows, statusItemId, statusPaused, statusEndSeconds, REGENERATION_ROLL_TYPE, STATUS_IDS } from '../janus-engine/statuses/expiry';
import { ftClockCalendarFromArr, ftCampaignNowSeconds } from '../shared/campaign-clock';
import { isWaterWet, waterWetHeld } from '../janus-engine/movement/water-wet';
import { at, values } from '../janus-engine/port/k5-render/support';
import { num } from './read-helpers';

type Row = Record<string, unknown>;
/** Scheduling hints only. Actual boundary work still uses the engine's tick /
 * expiry plans. Reuse those readers; PHP never decides from cached statistics. */
export function ftCreatureHeartbeatReasons(caster: CachedCaster): string[] {
    const entries = ['passive_ability', 'full_ability', 'ability', 'quick_ability', 'free_ability', 'reaction_ability'].flatMap(type =>
        abilityEntriesOfType(caster, type).map(entry => ({ id: abilityEntryId(entry), type, definition: null,
            charges: num(abilityEntryCharges(entry, 0)), max: num(abilityEntryMaxCharges(entry, 0)), inactive: !passiveActive(entry) })));
    const charges = chargeTicksFor(caster as Row, entries, {
        value: (row, key) => ftLayerValue(row, key), entries: (row, key) => ftLayerEntries(row, key),
        definition: id => { try { return sheetDefinition(caster, id); } catch { return null; } },
    });
    const reasons = charges.ticks.map(tick => ({ psi: 'psion', melody: 'bard', mastery: 'wizard' })[tick.kind]);
    if (charges.gates.length) reasons.push('wizard');
    const current = num(ftComputedValue(caster, 'vitality', 'v_current'));
    const maximum = num(ftComputedValue(caster, 'vitality', 'v_max'));
    const effective = num(ftComputedValue(caster, 'vitality', 'effective_v_max'), maximum);
    const target = statusRows(caster as Row, 'target');
    const withering = target.some(status => statusItemId(status) === '83EAD975-053D-1DDA-4474-F2128633500E');
    const regen = (entry: unknown): boolean => {
        try { return String(ftLayerValue(sheetDefinition(caster, entry), 'roll_type') ?? '') === REGENERATION_ROLL_TYPE; }
        catch { return true; }
    };
    const cannot = num(ftEncounterCreatureField(caster.encounter_creatures, 'encounter_creature_dead')) !== 0 || current <= 0
        || target.some(status => statusItemId(status) === '9FEE2E8A-9CF7-D74D-5D16-B86C2603A1FA');
    if (!cannot && !withering && ((current < effective && target.some(status => regen(statusItemId(status)) || (at(status, 'status_origin_id') && regen(at(status, 'status_origin_id')))))
        || (current < maximum && abilityEntriesOfType(caster, 'passive_ability').some(entry => passiveActive(entry) && regen(entry))))) reasons.push('regen');
    return [...new Set(reasons.filter(Boolean))];
}

export function ftRefreshCreatureHeartbeat(input: Row): Row {
    if (!Object.prototype.hasOwnProperty.call(input, 'pc_computed') && !Array.isArray(input.status_rows)) return input;
    const pcs: Record<string, string[]> = {};
    const info = input.encounter as { id?: string; in_combat?: boolean; reasons?: string[] } | null;
    const focused = typeof window.ftGetActiveEncounterId === 'function' ? String(window.ftGetActiveEncounterId() ?? '').toUpperCase() : info?.id;
    const held = focused ? window.FateTide?.encounterCache?.get(focused) as Row | undefined : undefined;
    const rows = values(at(held, 'encounter_creatures_arr')).filter((row): row is Row => !!row && typeof row === 'object');
    for (const row of rows) if (String(ftItemsCol(row, 'item_type')) === 'player_character') {
        const id = String(ftEncounterCreatureField(row.encounter_creatures, 'encounter_creature_id') ?? '');
        if (id) pcs[id] = ftCreatureHeartbeatReasons(row as CachedCaster);
    }
    const encounter = held ? { id: focused, in_combat: num(at(held, 'encounters', 'in_combat')) === 1, reasons: heartbeatEncounterReasons(rows, {
            value: (row, key) => ftLayerValue(row, key),
            definition: id => { try { return sheetDefinition(held, id); } catch { return null; } },
        }) } : info;
    const campaignId = String(at(held, 'encounters', 'encounter_master_parent_id') ?? '').toUpperCase();
    const calendar = ftClockCalendarFromArr(window.FateTide?.campaignCache?.get(campaignId));
    const statuses = Array.isArray(input.status_rows) && info?.id === focused ? input.status_rows as Row[] : rows.flatMap(row => statusRows(row, 'target'));
    const timed = statuses.flatMap(status => {
        if (statusPaused(status)) return [];
        const target = String(status.status_target_id ?? '').toUpperCase();
        const holder = rows.find(row => String(ftEncounterCreatureField(row.encounter_creatures, 'encounter_creature_id')) === target);
        if (isWaterWet(status) && holder && held && waterWetHeld(held, holder)) return [];
        const end = statusEndSeconds(status, { seconds: clock => ftCampaignNowSeconds(clock, calendar) });
        return end === null ? [] : [{ id: String(status.status_id), target, end }];
    });
    return { ...input, pcs, encounter, timed, crafting_wip: statuses.some(s => statusItemId(s) === STATUS_IDS.crafting) };
}

Object.assign(window, { ftRefreshCreatureHeartbeat });
